import { msg } from "@lingui/core/macro";
import { type ComponentProps, createEffect, type JSX, Show } from "solid-js";
import { RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@stockpilot/shared";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Theme-aware "broken crate / fallen boxes" illustration for error fallbacks.
 * Draws with `currentColor` + the accent token so it adapts to every theme.
 */
function BrokenCrateIllustration(props: ComponentProps<"svg">) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={cn("text-destructive/70", props.class)}
      {...props}
    >
      <g stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
        {/* Ground line */}
        <path d="M20 132h120" opacity="0.4" />
        {/* Tipped-over crate */}
        <g transform="rotate(-12 70 100)">
          <rect
            x="38"
            y="78"
            width="60"
            height="46"
            rx="4"
            fill="hsl(var(--destructive))"
            opacity="0.08"
          />
          <path d="M38 96h60M58 78v46M78 78v46" opacity="0.5" />
        </g>
        {/* Spilled box */}
        <rect
          x="100"
          y="96"
          width="30"
          height="28"
          rx="3"
          fill="hsl(var(--accent))"
          opacity="0.1"
        />
        <path d="M100 106h30" opacity="0.5" />
        {/* Alert spark */}
        <path d="M80 30v22M80 62v.5" stroke="currentColor" stroke-width="6" />
      </g>
    </svg>
  );
}

interface ErrorBoundaryFallbackProps {
  error: unknown;
  reset: () => void;
  /** Compact variant for inline data regions (no full-screen centering). */
  inline?: boolean;
  /** Override the title. */
  title?: string;
  /** Override the description. */
  description?: string;
  class?: string;
}

/**
 * Premium, theme-aware fallback used by every <ErrorBoundary> in the app.
 * Logs the error, shows a friendly message and offers Retry / Reload. Never a
 * blank screen.
 */
export function ErrorBoundaryFallback(props: ErrorBoundaryFallbackProps): JSX.Element {
  const { t } = useI18n();

  createEffect(() => {
    console.error("[ErrorBoundary] Caught render error:", props.error);
  });

  const message = () => {
    if (props.description) {
      return props.description;
    }
    if (props.error instanceof Error && props.error.message) {
      return props.error.message;
    }
    return t(msg`An unexpected error occurred while loading this section.`);
  };

  return (
    <div
      role="alert"
      class={cn(
        "flex flex-col items-center justify-center gap-5 text-center",
        props.inline
          ? "p-8 rounded-xl border border-destructive/20 bg-destructive/5"
          : "flex-1 p-8",
        props.class,
      )}
      data-testid="error-boundary-fallback"
    >
      <div class="animate-scale-in">
        <BrokenCrateIllustration class={props.inline ? "h-24 w-24" : "h-36 w-36"} />
      </div>
      <div class="space-y-2">
        <h2 class={cn("font-bold tracking-tight", props.inline ? "text-lg" : "text-2xl")}>
          {props.title ?? t(msg`Something went wrong`)}
        </h2>
        <p class="mx-auto max-w-sm text-sm text-muted-foreground">{message()}</p>
      </div>
      <div class="flex flex-wrap items-center justify-center gap-3">
        <Button class="gap-2" onClick={() => props.reset()} data-testid="error-boundary-retry">
          <RotateCcw class="h-4 w-4" />
          {t(msg`Retry`)}
        </Button>
        <Show when={!props.inline}>
          <Button
            variant="outline"
            class="gap-2"
            onClick={() => globalThis.location.reload()}
            data-testid="error-boundary-reload"
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Reload page`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
